<template>
  <v-hover>
    <tr
      slot-scope="{ hover }"
      :data-e2e-id="'trial-summary-' + item.trialId"
      :class="{ active: selectedTrialId === item.trialId }"
      @click="selectTrial(item.trialId)"
    >
      <td class="px-0">
        <v-icon right :color="favoriteColor(item)" @click.prevent.stop="changeFavorite(item)">{{
          favoriteIcon(item)
        }}</v-icon>
      </td>
      <td :class="nested ? 'pl-8 pr-0' : 'pl-2 pr-0'">
        <v-container>
          <v-row no-gutters align="center">
            <template v-if="isOverallParentSummary(item)">
              <span class="font-weight-bold">
                {{ item.displayName }}
                <app-tooltip color="dark" open-delay="500" right max-width="250px">
                  <template #activator="{ on }">
                    <v-icon small v-on="on">info</v-icon>
                  </template>
                  <span
                    >Aggregate of data from all cohorts. For individual cohort statistics, see
                    below.</span
                  >
                </app-tooltip>
              </span>
            </template>
            <template v-else>
              <v-row no-gutters justify="center">
                <v-col cols="1" class="mr-6 mr-sm-5 mr-md-4 mr-lg-3 mr-xl-2">
                  <trial-status-icon
                    v-if="item.organizationTrialStatus"
                    class="mr-2"
                    :status="item.organizationTrialStatus"
                  />
                </v-col>
                <v-col class="mt-1 font-weight-bold">
                  {{ item.displayName }}
                </v-col>
              </v-row>
            </template>
          </v-row>
          <v-row no-gutters>
            <div>
              <i>{{ sponsorName(item.sponsorName) }}</i>
            </div>
          </v-row>
        </v-container>
        <!--Pre Screening start-->
      </td>

      <td :class="['text-right', 'border-right', prescreeningClass]">
        {{ item.averageDaysPreScreening }} days
      </td>
      <td :class="['text-right', prescreeningClass]">{{ item.averageDaysAwaitingReview }} days</td>
      <td :class="['text-right', 'border-right', prescreeningClass]">
        {{ item.averageDaysInReview }} days
      </td>
      <td :class="['text-right', prescreeningClass]">
        {{ item.patientCount }}
      </td>
      <td :class="['text-right', prescreeningClass]">
        {{ item.patientAwaitingReviewCount }} ({{ item.patientAwaitingReviewIdentifiedPercent }}%)
      </td>
      <td :class="['text-right', prescreeningClass]">
        {{ item.patientInReviewCount }} ({{ item.patientInReviewIdentifiedPercent }}%)
      </td>
      <td :class="['text-right', prescreeningClass]">
        {{ item.patientCompletedCount }} ({{ item.patientCompletedIdentifiedPercent }}%)
      </td>
      <!--Pre Screening end-->
      <!--Summary start-->
      <td :class="summaryClass">
        {{ formatDuration(item.currentDuration) | orDefault }}
      </td>
      <td
        :class="[
          'text-right',
          summaryClass,
          {
            negative: item.monthlyEnrollmentDifference && item.monthlyEnrollmentDifference < 0,
            positive: item.monthlyEnrollmentDifference && item.monthlyEnrollmentDifference >= 0
          }
        ]"
      >
        {{ formatNumber(item.monthlyEnrollmentDifference) | orDefault
        }}<span v-if="item.monthlyEnrollmentDifference">%</span>
      </td>
      <td :class="['text-right', summaryClass]">{{ item.averageDaysPreScreening }} days</td>
      <td :class="['border-right', 'text-right', summaryClass]">
        {{ item.patientCompletedCount }}
      </td>
      <td :class="['text-right', summaryClass]">
        {{ item.patientFollowingCount }}
      </td>
      <td :class="['border-right', 'text-right', summaryClass]">
        {{ item.patientMatchedCount }}
      </td>
      <td :class="['text-right', summaryClass]">
        {{ item.patientEnrolledCount }} ({{ item.patientEnrolledPercent }}%)
      </td>
      <td :class="['text-right', summaryClass]">
        {{ item.patientNotConsentedCount }} ({{ item.patientNotConsentedPercent }}%)
      </td>
      <td :class="['text-right', summaryClass]">
        {{ item.patientNotEligibleCount }} ({{ item.patientNotEligiblePercent }}%)
      </td>
      <!--Summary end-->
      <!--enrollment start-->
      <td :class="[enrollmentClass]">
        {{ formatDuration(item.currentDuration) | orDefault }}
      </td>
      <td :class="[enrollmentClass]">
        {{ formatDuration(item.targetDuration) | orDefault }}
      </td>
      <td :class="['text-right', enrollmentMonthlyClass]">
        {{ formatEnrollment(item.targetMonthlyEnrollment) | orDefault }}
      </td>
      <td
        :class="[
          'text-right',
          enrollmentMonthlyClass,
          {
            negative:
              item.averageMonthlyEnrollment &&
              item.averageMonthlyEnrollment < item.targetMonthlyEnrollment,
            positive:
              item.averageMonthlyEnrollment &&
              item.averageMonthlyEnrollment >= item.targetMonthlyEnrollment
          }
        ]"
      >
        {{ formatEnrollment(item.averageMonthlyEnrollment) | orDefault }}
      </td>
      <td :class="['text-right', enrollmentTotalClass]">
        {{ formatEnrollment(item.plannedEnrollmentToDate) | orDefault }}
      </td>
      <td
        class="text-right"
        :class="[
          enrollmentTotalClass,
          {
            negative: item.enrollmentDifference < 0,
            positive: item.enrollmentDifference >= 0
          }
        ]"
      >
        {{ item.patientEnrolledCount }}
      </td>
      <td :class="['border-bottom', 'text-right', enrollmentProjectedClass]">
        {{ item.targetEnrollment | orDefault }}
      </td>
      <td
        :class="[
          'border-bottom',
          'text-right',
          enrollmentProjectedClass,
          {
            negative:
              item.projectedTotalEnrollment &&
              item.projectedTotalEnrollment < item.targetEnrollment,
            positive:
              item.projectedTotalEnrollment &&
              item.projectedTotalEnrollment >= item.targetEnrollment
          }
        ]"
      >
        {{ formatNumber(item.projectedTotalEnrollment) | orDefault }}
      </td>
      <td
        :class="[
          'text-right',
          enrollmentClass,
          {
            negative: item.monthlyEnrollmentDifference && item.monthlyEnrollmentDifference < 0,
            positive: item.monthlyEnrollmentDifference && item.monthlyEnrollmentDifference >= 0
          }
        ]"
      >
        {{ formatNumber(item.monthlyEnrollmentDifference) | orDefault
        }}<span v-if="item.monthlyEnrollmentDifference">%</span>
      </td>
      <!--enrollment end-->
      <!--Distribution start-->
      <td :class="['text-right', 'border-right', distributionClass]">
        {{ item.patientCount }}
      </td>
      <td :class="['text-right', distributionClass]">
        {{ item.patientAwaitingReviewCount }}
      </td>
      <td :class="['text-right', 'border-right', distributionClass]">
        {{ item.patientInReviewCount }}
      </td>
      <td :class="['text-right', distributionClass]">
        {{ item.patientFollowingCount }}
      </td>
      <td :class="['text-right', 'border-right', distributionClass]">
        {{ item.patientMatchedCount }}
      </td>
      <td :class="['text-right', distributionClass]">
        {{ item.patientEnrolledCount }}
      </td>
      <td :class="['text-right', distributionClass]">
        {{ item.patientNotConsentedCount }}
      </td>
      <td :class="['text-right', distributionClass]">
        {{ item.patientNotEligibleCount }}
      </td>
      <!--Distribution end-->
      <td class="px-0">
        <site-trial-edit-dialog
          v-if="hover || selectedTrialId === item.trialId"
          :key="item.key"
          :edit-target-enrollment="item.summaryType === 'TRIAL'"
          :organization-id="item.organizationId"
          :name="item.organizationName"
          :trial-id="item.trialId"
          :friendly-name="item.displayName"
          :trial-external-id="item.trialExternalId"
          :recruiting-start-date="item.organizationRecruitingStartDate"
          :recruiting-end-date="
            item.organizationRecruitingEndDate || item.trialPrimaryCompletionDate
          "
          :target-enrollment="item.targetEnrollment"
          :org-trial-external-id="item.organizationExternalId"
          :trial-link="item.trialLink"
          :current-care-team="item.organizationTrialCareTeam"
          :org-trial-id="item.organizationTrialId"
          :potential-care-team-members="potentialCareTeamMembers"
          :status="item.organizationTrialStatus"
          :trial-type="item.summaryType"
          :parent-org-trial-external-id="item.organizationParentTrialExternalId"
          @edit-site-trial="onEditSiteTrial"
          @click="selectTrial"
        />
      </td>
    </tr>
  </v-hover>
</template>

<script>
import TrialStatusIcon from '@/components/trials/TrialStatusIcon'
import { durationMixin } from '@/mixins/duration'
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import _ from 'lodash'
import { ENROLLMENT_PRECISION } from '@/helpers/table-helper'
import SiteTrialEditDialog from '@/components/insights/SiteTrialEditDialog'

export default {
  name: 'InsightsTableRow',
  components: {
    TrialStatusIcon,
    SiteTrialEditDialog
  },
  mixins: [durationMixin],
  props: {
    item: { type: Object, required: true },
    selectedTrialId: { type: String, required: false, default: null },
    potentialCareTeamMembers: { type: Array, required: false, default: () => [] },
    nested: { type: Boolean, required: false },
    prescreeningClass: { type: String, required: false, default: '' },
    summaryClass: { type: String, required: false, default: '' },
    enrollmentClass: { type: String, required: false, default: '' },
    distributionClass: { type: String, required: false, default: '' },
    enrollmentMonthlyClass: { type: String, required: false, default: '' },
    enrollmentTotalClass: { type: String, required: false, default: '' },
    enrollmentProjectedClass: { type: String, required: false, default: '' }
  },
  methods: {
    isOverallParentSummary(summary) {
      return summary.children?.length > 0
    },
    sponsorName(name) {
      return MyActiveOrganization.name !== name ? name : null
    },
    formatNumber(number, digits = 0) {
      return _.isNil(number) ? number : number.toFixed(digits)
    },
    formatEnrollment(number) {
      return this.formatNumber(number, ENROLLMENT_PRECISION)
    },
    selectTrial(trialId) {
      this.$emit('select-trial', trialId)
    },
    onEditTrial(trial) {
      this.$emit('on-edit-trial', trial)
    },
    navigateTo(trialId, isParentSummary) {
      this.$emit('on-trial-route-click', trialId, isParentSummary)
    },
    getTrialType(summary) {
      return summary.summaryType
    },
    favoriteIcon(summary) {
      return summary.favorite ? 'mdi-star' : 'mdi-star-outline'
    },
    favoriteColor(summary) {
      return summary.favorite ? 'favoritedColor' : null
    },
    changeFavorite(summary) {
      this.$emit('change-favorite', summary)
    },
    onEditSiteTrial(site) {
      this.$emit('on-edit-trial', site)
    }
  }
}
</script>

<style scoped lang="scss">
.positive {
  color: var(--v-success-darken1);
}
.negative {
  color: var(--v-error-base);
}
.border-right {
  border-right: solid var(--v-dark-base) 1px;
}
</style>
